import React, { useEffect, useState, Suspense } from "react";
import { getYear, getMonth } from "date-fns";
import range from "lodash/range";
import css from "./HorseBirthDatePicker.module.css";
import IconChevronLeft from "../IconChevronLeft/IconChevronLeft";
import IconChevronRight from "../IconChevronRight/IconChevronRight";
import { useIntl } from "react-intl";
import IconCalendar from "../IconCalendar/IconCalendar";

const DatePicker = React.lazy(() => import("react-datepicker"));

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const years = range(1990, getYear(new Date()) + 1, 1);

const CustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
}) => (
    <div
        style={{
            margin: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
        }}
    >
        <button
            type="button"
            className={css.btnContainer}
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
        >
            <IconChevronLeft />
        </button>

        <select
            value={getYear(date)}
            onChange={({ target: { value } }) => changeYear(+value)}
        >
            {years.map((option) => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>

        <select
            value={MONTHS[getMonth(date)]}
            onChange={({ target: { value } }) => changeMonth(MONTHS.indexOf(value))}
        >
            {MONTHS.map((option) => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>

        <button
            type="button"
            className={css.btnContainer}
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
        >
            <IconChevronRight />
        </button>
    </div>
);

const ClientOnly = ({ children, fallback = null }) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    return mounted ? children : fallback;
};

export const HorseBirthDatePicker = ({ input, meta }) => {
    const intl = useIntl();
    const [selectedDate, setSelectedDate] = useState(
        input.value ? new Date(input.value) : null
    );

    useEffect(() => {
        if (typeof window !== "undefined") {
            import("react-datepicker/dist/react-datepicker.css");
        }
    }, []);

    const handleChange = (date) => {
        setSelectedDate(date);
        input.onChange(date);
    };

    return (
        <div className={css.fieldDatePicker}>
            <label className={css.label}>
                {intl.formatMessage({ id: "CustomExtendedDataField.horseBirthDateLabel" })}
            </label>

            <div className={css.calenderContainer}>
                <IconCalendar />
            </div>

            {/* Client-only render to prevent SSR crash */}
            <ClientOnly
                fallback={
                    <input
                        id={input.name}
                        placeholder={intl.formatMessage({
                            id: "CustomExtendedDataField.horseBirthDatePlaceholder",
                        })}
                        className={css.dateInput}
                        readOnly
                    />
                }
            >
                <Suspense
                    fallback={
                        <input
                            id={input.name}
                            className={css.dateInput}
                            readOnly
                        />
                    }
                >
                    <DatePicker
                        id={input.name}
                        selected={selectedDate}
                        onChange={handleChange}
                        renderCustomHeader={(props) => <CustomHeader {...props} />}
                        dateFormat="dd/MM/yyyy"
                        placeholderText={intl.formatMessage({
                            id: "CustomExtendedDataField.horseBirthDatePlaceholder",
                        })}
                        showPopperArrow={false}
                        required
                        className={css.dateInput}
                    />
                </Suspense>
            </ClientOnly>

            {meta.touched && meta.error ? (
                <span className={css.error}>{meta.error}</span>
            ) : null}
        </div>
    );
};
